/* eslint-disable */
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { compact, isArray, omit } from "lodash";

import {
  DeleteResponse,
  HTTP_PATH,
  Meta,
  QueryParamFilter,
  QueryParamProccesed,
  QueryParams,
  QueryParamSorters,
  QueryParamV2,
  SORT_ORDER,
} from "network/models/api.model";
import { ENTITIES } from "network/models/entities";
import ArrayUtil from "utils/array.util";
import { IUser, IUserLogin } from "network/models/user.model";
import { ITier } from "network/models/tier.model";
import { IRule } from "network/models/points.rules.model";
import { IFamily, IFamilyResponse } from "network/models/family.model";

type Input = {
  config: AxiosRequestConfig;
  interceptors?: {
    request?: (
      config: AxiosRequestConfig
    ) => Promise<AxiosRequestConfig> | AxiosRequestConfig;
    response?: {
      onFulfilled?: (value: AxiosResponse<any>) => AxiosResponse<any>;
      onRejected?: (error: AxiosError) => Promise<AxiosError>;
    };
  };
};

type GetEntityProp = {
  entity: string;
  params?: QueryParams | undefined;
  byIdKey?: string;
};

export default class Api {
  public axios: AxiosInstance;
  static instance: Api;
  static REACT_APP_API_BASE_URL: string;

  static initApiV2(baseUrl: string) {
    Api.REACT_APP_API_BASE_URL = baseUrl as string;
  }

  static init({ config, interceptors }: Input) {
    Api.instance = new Api({
      config,
      interceptors,
    });
  }

  constructor({ config, interceptors }: Input) {
    this.axios = axios.create(config);
    if (!!interceptors?.request) {
      //@ts-ignore
      this.axios.interceptors.request.use(interceptors?.request);
    }
    if (!!interceptors?.response) {
      this.axios.interceptors.response.use(
        interceptors?.response?.onFulfilled,
        interceptors?.response?.onRejected
      );
    }
  }

  public get = <T>(url: string, config?: AxiosRequestConfig | undefined) => {
    return this.axios.get<T>(url, config);
  };

  public post = <T>(
    url: string,
    payload: T,
    config?: AxiosRequestConfig | undefined
  ) => {
    return this.axios.post<T>(url, payload, config);
  };

  public put = <T>(
    url: string,
    payload: T,
    config?: AxiosRequestConfig | undefined
  ) => {
    return this.axios.put<T>(url, payload, config);
  };

  public delete = <T>(url: string, config?: AxiosRequestConfig | undefined) => {
    return this.axios.delete<T>(url, config);
  };

  public convertArrayToParamsValue = <T>(
    items: Array<T | undefined> | undefined,
    delimiter = "|"
  ) =>
    items?.reduce(
      (acc, c, index) => (index === 0 ? acc + c : acc + delimiter + c),
      ""
    );

  public processFilterValues = (
    items: Array<string | number> | string | number | undefined
    //@ts-ignore
  ) => (isArray(items) ? this.convertArrayToParamsValue(items, ";") : items);

  public processFilters = (
    filter: Array<QueryParamFilter> | undefined
  ): {
    filter_cols?: string | number;
    filter_ops?: string | number;
    filter_vals?: string | number;
  } => {
    return (
      filter?.reduce(
        (acc, p, index) => {
          const filterValue = this.processFilterValues(p?.filter_val);
          return {
            filter_cols:
              index === 0
                ? acc.filter_cols + p?.filter_col
                : acc.filter_cols + "|" + p?.filter_col,
            filter_ops:
              index === 0
                ? acc.filter_ops + p?.filter_op
                : acc.filter_ops + "|" + p?.filter_op,
            filter_vals:
              index === 0
                ? acc.filter_vals + filterValue
                : acc.filter_vals + "|" + filterValue,
          };
        },
        {
          filter_cols: "",
          filter_ops: "",
          filter_vals: "",
        }
      ) ?? {}
    );
  };

  public processSorters = (sorters: Array<QueryParamSorters> | undefined) => {
    return (
      sorters?.reduce(
        (acc, sorter, index) => {
          return {
            sort_col:
              index === 0
                ? acc.sort_col + sorter?.sort_col
                : acc.sort_col + "|" + sorter?.sort_col,
            sort_order:
              index === 0
                ? acc.sort_order + sorter?.sort_order
                : acc.sort_order + "|" + sorter?.sort_order,
          };
        },
        { sort_col: "", sort_order: "" }
      ) ?? []
    );
  };

  public processParams = (
    queryParams?: QueryParams | undefined
  ): QueryParamProccesed => {
    const params = {
      ...omit(queryParams, ["filter", "sorters"]),
      //@ts-ignore
      children: this.convertArrayToParamsValue<string>(queryParams?.children),
      ds_cols: this.convertArrayToParamsValue<string>(queryParams?.ds_col),
      ...this.processFilters(queryParams?.filter),
      ...this.processSorters(queryParams?.sorters),
    };
    return params;
  };

  public getEntities = async <T>({
    entity,
    params,
    byIdKey,
  }: GetEntityProp) => {
    const response = await this.get<{
      data: Array<T>;
      meta: Meta;
    }>(entity, { params: this.processParams(params) });
    return {
      ...response,
      data: {
        ...response?.data,
        byId: byIdKey
          ? ArrayUtil.generateMap<T>(response?.data?.data, byIdKey)
          : undefined,
      },
    };
  };
  public getEntity = <T>(entity: ENTITIES, id: string) => {
    return this.get<{
      data: T;
      meta: Meta;
    }>(`${entity}`);
  };

  public createEntity = async <T>(entity: ENTITIES, payload: T) => {
    try {
      console.log("Payload being sent:", payload); // Debugging: Check payload before sending

      const response = await this.post(
        `${entity}`,
        JSON.stringify(payload), // Ensure payload is a string
        {
          headers: {
            "Content-Type": "application/json", // Important for sending JSON
          },
        }
      );

      return response; // No need to spread if response is already an object
    } catch (error) {
      console.error("Error in createEntity:", error);
      throw error; // Rethrow error to handle it elsewhere
    }
  };

  public updateEntity = <T>(entity: ENTITIES | string, payload: T) => {
    return this.put<T>(`${entity}`, payload);
  };

  public deleteEntity = (entity: ENTITIES, id: string) => {
    return this.delete<DeleteResponse>(`${entity}/${HTTP_PATH.ID}/${id}`);
  };

  public login = async (payload: Partial<IUserLogin>) => {
    return (await this.createEntity(ENTITIES.LOG_IN, payload))?.data;
  };

  public getAllFamilies = async ({
    page,
    limit,
  }: {
    page: number;
    limit: number;
  }) => {
    const response = await this.get<IFamilyResponse>(
      `${ENTITIES.GET_ALL_FAMILIES}?page=${page}&limit=${limit}`
    );
    return response.data;
  };

  public getFamily = async (id: string) => {
    const response = await this.get<IFamilyResponse>(
      `${ENTITIES.GET_FAMILY}/${id}`
    );
    return response.data;
  };
  public createFamily = async (payload: IFamily) => {
    const response = await this.createEntity(ENTITIES.CREATE_FAMILY, payload);
    return response.data;
  };
  public updateFamily = async (payload: IFamily) => {
    const response = await this.updateEntity(
      `${ENTITIES.UPDATE_FAMILY}/${payload._id}`,
      payload
    );
    return response.data;
  };
  public deleteFamily = async (id: string) => {
    const response = await this.deleteEntity(ENTITIES.DELETE_FAMILY, id);
    return response.data;
  };

  public getTiers = async () => {
    const response = await this.get<ITier>(ENTITIES.GET_TIERS);
    return response.data;
  };
  public getPointsRules = async () => {
    const response = await this.get<IRule>(ENTITIES.GET_RULES);
    return response.data;
  };

  public getUserPointsDetails = async ({ userId }: { userId: string }) => {
    const response = await this.get<IRule>(
      `${ENTITIES.GET_USER_POINTS}/${userId}`
    );
    return response.data;
  };

  public getUsers = async () => {
    const CancelToken = axios.CancelToken;
    let cancel;

    try {
      const response = await this.get<IUser>(`${ENTITIES.GET_USERS}`, {
        cancelToken: new CancelToken((c) => {
          cancel = c; // Store the cancel function
        }),
        // headers,
      });
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Request canceled", error.message);
      } else {
        console.error("Error fetching users", error);
      }
    }
  };

  public addPointsRule = async (payload: Partial<IRule>) => {
    return (await this.createEntity(ENTITIES.ADD_RULES, payload))?.data;
  };
}
